import { apiFetch, fetchCurrentUser, refreshTokenIfNeeded, type AuthUser } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || '';

export type AccountProfile = {
  id: string;
  username?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
};

export type AccountResponse = {
  account: AccountProfile;
  auth?: {
    userId: string;
    email?: string;
    name?: string;
    organizationId?: string;
    orgRoles?: string[];
    platformRoles?: string[];
    permissions?: string[];
  };
  accountManagementAvailable?: boolean;
  accountManagementMessage?: string;
};

export type UpdateAccountInput = {
  username?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
};

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof data?.message === 'string' ? data.message : 'Request failed';
    throw new Error(message);
  }
  return data as T;
}

export async function loadAccountProfile(): Promise<AccountResponse> {
  const response = await apiFetch(`${API_BASE}/api/auth/account`, {
    method: 'GET',
  });

  return readJson<AccountResponse>(response);
}

export async function updateAccountProfile(input: UpdateAccountInput): Promise<{
  account: AccountProfile;
  user: AuthUser | null;
}> {
  const response = await apiFetch(`${API_BASE}/api/auth/account`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  const data = await readJson<{ ok: true; account: AccountProfile }>(response);

  let user: AuthUser | null = null;
  try {
    await refreshTokenIfNeeded();
    user = await fetchCurrentUser();
  } catch {
    user = null;
  }

  return {
    account: data.account,
    user,
  };
}

export async function changeAccountPassword(input: ChangePasswordInput): Promise<{ ok: true; requiresLogin?: boolean }> {
  const response = await apiFetch(`${API_BASE}/api/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  return readJson<{ ok: true; requiresLogin?: boolean }>(response);
}
